import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { PANEL_KEYS, type PanelKeySchema, type PanelPricingCell } from '@wrapforce/shared/schemas/pricing';
import { SIZE_CLASSES } from '@wrapforce/shared/constants';
import { usePanelPricingMatrix, useUpdatePanelOverride, useDeletePanelOverride } from './api';
import { SIZE_CLASS_LABELS } from '@/features/quotes/pricing/VehiclePicker';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input, Label } from '@/components/ui/input';
import { cn } from '@/lib/cn';

export const PANEL_LABEL_KEYS: Record<PanelKeySchema, string> = {
  hood: 'panelPricing.panel.hood',
  roof: 'panelPricing.panel.roof',
  trunk_tailgate: 'panelPricing.panel.trunkTailgate',
  front_bumper: 'panelPricing.panel.frontBumper',
  rear_bumper: 'panelPricing.panel.rearBumper',
  fenders: 'panelPricing.panel.fenders',
  doors: 'panelPricing.panel.doors',
  rockers: 'panelPricing.panel.rockers',
  quarter_panels: 'panelPricing.panel.quarterPanels',
  mirrors: 'panelPricing.panel.mirrors',
  a_pillars: 'panelPricing.panel.aPillars',
  grille_surround: 'panelPricing.panel.grilleSurround',
  badges_delete: 'panelPricing.panel.badgesDelete',
};

export function PanelPricingTab({ workspaceId }: { workspaceId: string | null }) {
  const { t } = useTranslation('pricingV2');
  const { data: cells } = usePanelPricingMatrix(workspaceId);
  const updateOverride = useUpdatePanelOverride(workspaceId);
  const deleteOverride = useDeletePanelOverride(workspaceId);
  const [sizeClass, setSizeClass] = useState<(typeof SIZE_CLASSES)[number]>(SIZE_CLASSES[0]);

  const cellByPanel = useMemo(() => {
    const map = new Map<string, PanelPricingCell>();
    for (const c of cells ?? []) if (c.sizeClass === sizeClass) map.set(c.panelKey, c);
    return map;
  }, [cells, sizeClass]);

  function saveFtX10(panelKey: PanelKeySchema, cell: PanelPricingCell | undefined, ftX10: number) {
    if (!cell || ftX10 === cell.ftX10) return;
    updateOverride.mutate(
      { panelKey, sizeClass, input: { ftX10, minutes: cell.minutes } },
      {
        onSuccess: () => toast.success(t('panelPricing.savedToast')),
        onError: () => toast.error(t('panelPricing.saveError')),
      },
    );
  }

  function saveMinutes(panelKey: PanelKeySchema, cell: PanelPricingCell | undefined, minutes: number) {
    if (!cell || minutes === cell.minutes) return;
    updateOverride.mutate(
      { panelKey, sizeClass, input: { ftX10: cell.ftX10, minutes } },
      {
        onSuccess: () => toast.success(t('panelPricing.savedToast')),
        onError: () => toast.error(t('panelPricing.saveError')),
      },
    );
  }

  function resetPanel(panelKey: PanelKeySchema) {
    deleteOverride.mutate(
      { panelKey, sizeClass },
      {
        onSuccess: () => toast.success(t('panelPricing.resetToast')),
        onError: () => toast.error(t('panelPricing.saveError')),
      },
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('panelPricing.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="panel-pricing-size-class">{t('panelPricing.sizeClassLabel')}</Label>
          <select
            id="panel-pricing-size-class"
            value={sizeClass}
            onChange={(e) => setSizeClass(e.target.value as (typeof SIZE_CLASSES)[number])}
            className="h-9 w-full border border-border bg-surface px-2 text-sm md:w-64"
          >
            {SIZE_CLASSES.map((sc) => (
              <option key={sc} value={sc}>{SIZE_CLASS_LABELS[sc]}</option>
            ))}
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr>
                <th className="border-b border-r border-border px-2 py-1.5 text-left font-bold uppercase text-muted">{t('panelPricing.panelHeader')}</th>
                <th className="border-b border-border px-2 py-1.5 text-right font-bold uppercase text-muted">{t('panelPricing.filmHeader')}</th>
                <th className="border-b border-border px-2 py-1.5 text-right font-bold uppercase text-muted">{t('panelPricing.labourHeader')}</th>
                <th className="border-b border-border px-2 py-1.5"></th>
              </tr>
            </thead>
            <tbody>
              {PANEL_KEYS.map((panelKey) => {
                const cell = cellByPanel.get(panelKey);
                return (
                  <tr key={panelKey}>
                    <td className="border-b border-r border-border px-2 py-1.5 font-semibold text-ink">
                      <div className="flex items-center gap-1.5">
                        <span>{t(PANEL_LABEL_KEYS[panelKey])}</span>
                        {cell?.isOverridden ? (
                          <span className="rounded-full bg-accent/10 px-1.5 py-0.5 text-[9px] font-bold uppercase text-accent">{t('panelPricing.override')}</span>
                        ) : null}
                      </div>
                    </td>
                    <td className={cn('border-b border-border px-2 py-1.5 text-right')}>
                      <Input
                        type="number"
                        min={0}
                        step="0.1"
                        defaultValue={cell ? (cell.ftX10 / 10).toFixed(1) : ''}
                        key={`${panelKey}-${sizeClass}-ft-${cell?.ftX10 ?? 'none'}`}
                        className="h-8 text-right"
                        onBlur={(e) => {
                          if (e.target.value.trim() === '') return;
                          const ftX10 = Math.round(Math.max(0, Number(e.target.value)) * 10);
                          saveFtX10(panelKey, cell, ftX10);
                        }}
                      />
                    </td>
                    <td className="border-b border-border px-2 py-1.5 text-right">
                      <Input
                        type="number"
                        min={0}
                        step="1"
                        defaultValue={cell ? cell.minutes : ''}
                        key={`${panelKey}-${sizeClass}-min-${cell?.minutes ?? 'none'}`}
                        className="h-8 text-right"
                        onBlur={(e) => {
                          if (e.target.value.trim() === '') return;
                          const minutes = Math.round(Math.max(0, Number(e.target.value)));
                          saveMinutes(panelKey, cell, minutes);
                        }}
                      />
                    </td>
                    <td className="border-b border-border px-2 py-1.5 text-right">
                      {cell?.isOverridden ? (
                        <button
                          type="button"
                          onClick={() => resetPanel(panelKey)}
                          className="text-xs font-semibold text-muted hover:text-ink"
                        >
                          {t('panelPricing.reset')}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-muted">{t('panelPricing.hint')}</p>
      </CardContent>
    </Card>
  );
}
